import React, { useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    CircularProgress,
    useTheme,
    Skeleton
} from '@mui/material';
import Dygraph from 'dygraphs';
import { format } from 'date-fns';

interface DeviceInfo {
    box: string;
    iccid: string;
    time_stamp?: string;
    operator?: string;
}

interface DeviceChartsProps {
    processedDevices: DeviceInfo[];
    deviceChartData: { [key: string]: any[] };
    deviceLoadings: { [key: string]: boolean };
    isDarkMode: boolean;
    selectedMetric: string;
    chartsPerRow?: number;
    timeRange?: number;
}

type DygraphOptions = {
    labels: string[];
    width: number;
    height: number;
    gridLineColor: string;
    gridLineWidth: number;
    series: {
        [key: string]: {
            axis: "" | "y1" | "y2";
            color?: string;
            strokeWidth?: number;
            plotter?: (e: any) => void;
            fillGraph?: boolean;
        };
    };
    axes: {
        y?: {
            axisLabelWidth?: number;
            axisLineColor?: string;
            gridLineColor?: string;
            gridLineWidth?: number;
        };
        y2?: {
            axisLabelWidth?: number;
            axisLineColor?: string;
            gridLineColor?: string;
            gridLineWidth?: number;
            independentTicks?: boolean;
        };
        x?: {
            axisLineColor?: string;
            gridLineColor?: string;
            gridLineWidth?: number;
        };
    };
    animatedZooms?: boolean;
    axisLabelColor?: string;
    plotter?: (e: any) => void;
    legendFormatter?: (data: any) => string;
    file?: any[][];
};

const DeviceCharts: React.FC<DeviceChartsProps> = ({
    processedDevices,
    deviceChartData,
    deviceLoadings,
    isDarkMode,
    selectedMetric,
    chartsPerRow = 3,
    timeRange = 2
}) => {
    const chartRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const dygraphInstances = useRef<{ [key: string]: Dygraph }>({});

    const formatLegendValue = (value: number | null): string => {
        if (value === null || value === undefined || isNaN(value)) return 'N/A';
        if (value > 1000) return value.toFixed(0);
        return value.toFixed(2);
    };

    const barChartPlotter = (e: any) => {
        const ctx = e.drawingContext;
        const points = e.points;
        const y_bottom = e.dygraph.toDomYCoord(0);

        ctx.fillStyle = e.color;
        ctx.strokeStyle = e.color;

        let min_sep = Infinity;
        for (let i = 1; i < points.length; i++) {
            const sep = points[i].canvasx - points[i - 1].canvasx;
            if (sep < min_sep) min_sep = sep;
        }
        const bar_width = Math.floor(2.0 / 3 * min_sep);

        for (let i = 0; i < points.length; i++) {
            const p = points[i];
            const center_x = p.canvasx;

            ctx.fillRect(center_x - bar_width / 2,
                p.canvasy,
                bar_width,
                y_bottom - p.canvasy);

            ctx.strokeRect(center_x - bar_width / 2,
                p.canvasy,
                bar_width,
                y_bottom - p.canvasy);
        }
    };

    const getChartOptions = (boxNumber: string) => {
        const element = chartRefs.current[boxNumber];
        if (!element || !deviceChartData[boxNumber]?.length) return null;

        let chartData;
        let options: DygraphOptions;

        const baseOptions: Partial<DygraphOptions> = {
            width: element.clientWidth,
            height: element.clientHeight,
            gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
            gridLineWidth: 1,
            axes: {
                y: {
                    axisLabelWidth: 35,
                    axisLineColor: isDarkMode ? '#666' : '#ccc',
                    gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                    gridLineWidth: 1
                },
                x: {
                    axisLineColor: isDarkMode ? '#666' : '#ccc',
                    gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                    gridLineWidth: 1
                }
            }
        };

        const commonLegendFormatter = (data: any) => {
            if (data.x == null) return '';

            const dateStr = format(new Date(data.xHTML), 'dd MMM yyyy HH:mm:ss');
            let html = `
                <div style="
                    background-color: ${isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)'};
                    padding: 4px 8px;
                    border-radius: 4px;
                    border: 1px solid ${isDarkMode ? '#666' : '#ccc'};
                    position: absolute;
                    top: 5px;
                    right: 5px;
                    z-index: 10;
                    white-space: nowrap;
                    font-size: 12px;
                ">
                    <strong>${dateStr}</strong>`;

            data.series.forEach((series: any) => {
                if (!series.isVisible) return;
                const formattedValue = formatLegendValue(series.y);
                html += ` | <span style="color: ${series.color};">${series.label}: ${formattedValue}</span>`;
            });

            html += '</div>';
            return html;
        };

        if (selectedMetric === 'loadUptime') {
            chartData = deviceChartData[boxNumber].map(item => [
                new Date(item.time),
                parseFloat(item.avg5mload) || null,
                parseFloat(item.uptime) || null
            ]);

            options = {
                ...baseOptions,
                labels: ['Time', 'Load', 'Uptime'],
                series: {
                    'Load': {
                        axis: 'y1',
                        color: '#FFD700',
                        strokeWidth: 2,
                        plotter: barChartPlotter,
                        fillGraph: true,
                    },
                    'Uptime': {
                        axis: 'y2',
                        color: '#FF0000',
                        strokeWidth: 2,
                    }
                },
                axes: {
                    ...baseOptions.axes,
                    y2: {
                        axisLabelWidth: 35,
                        axisLineColor: isDarkMode ? '#666' : '#ccc',
                        gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                        gridLineWidth: 1,
                        independentTicks: true
                    }
                },
                legendFormatter: commonLegendFormatter,
            } as DygraphOptions;
        } else if (selectedMetric === 'JAS') {
            chartData = deviceChartData[boxNumber].map(item => [
                new Date(item.time),
                item.JAS_ABS || null,
                item.JAS_VALUE || null
            ]);

            options = {
                ...baseOptions,
                labels: ['Time', 'JAS ABS', 'JAS VALUE'],
                series: {
                    'JAS ABS': { 
                        axis: 'y1',
                        color: '#82ca9d'
                    },
                    'JAS VALUE': { 
                        axis: 'y2',
                        color: '#FFFF00'
                    }
                },
                axes: {
                    ...baseOptions.axes,
                    y2: {
                        axisLabelWidth: 35,
                        axisLineColor: isDarkMode ? '#666' : '#ccc',
                        gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                        gridLineWidth: 1,
                        independentTicks: true
                    }
                },
                legendFormatter: commonLegendFormatter,
            } as DygraphOptions;
        } else {
            chartData = deviceChartData[boxNumber].map(item => [
                new Date(item.time),
                item[selectedMetric] || null
            ]);

            options = {
                ...baseOptions,
                labels: ['Time', selectedMetric],
                series: {
                    [selectedMetric]: {
                        axis: 'y1',
                        color: getMetricColor(selectedMetric),
                        strokeWidth: 2
                    }
                },
                legendFormatter: commonLegendFormatter,
            } as DygraphOptions;
        }

        return { chartData, options };
    };

    const getMetricColor = (metric: string) => {
        const colors = {
            PM10: '#8884d8',
            PM25: '#82ca9d',
            AMBIENT_TEMP: '#ff7300',
            AMBIENT_HUMID: '#0088FE',
            AMBIENT_PRESS: '#A52A2A',
            DUST_HEATER_NTC: '#FF6347',
            NH3_raw: '#9C27B0',
            H2S_raw: '#FF9800',
            ODO: '#00BCD4',
            rssi: '#66ff66',
            snr: '#66ccff',
            wifi_signal: '#cc99ff'
        };
        return colors[metric] || '#666666';
    };

    useEffect(() => {
        // Usuń wykresy, które nie są już wyświetlane
        Object.keys(dygraphInstances.current).forEach(boxNumber => {
            if (!processedDevices.some(device => device.box === boxNumber)) {
                dygraphInstances.current[boxNumber]?.destroy();
                delete dygraphInstances.current[boxNumber];
            }
        });

        // Inicjalizuj lub aktualizuj wykresy dla aktualnych urządzeń
        processedDevices.forEach(device => {
            const chartConfig = getChartOptions(device.box);
            if (!chartConfig) return;

            const { chartData, options } = chartConfig;

            try {
                if (dygraphInstances.current[device.box]) {
                    dygraphInstances.current[device.box].updateOptions({
                        file: chartData,
                        ...options
                    });
                } else {
                    if (chartRefs.current[device.box]) {
                        dygraphInstances.current[device.box] = new Dygraph(
                            chartRefs.current[device.box]!,
                            chartData,
                            options
                        );
                    }
                }
            } catch (error) {
                console.error(`Error initializing chart for box ${device.box}:`, error);
            }
        });

        return () => {
            Object.values(dygraphInstances.current).forEach(instance => {
                instance.destroy();
            });
            dygraphInstances.current = {};
        };
    }, [deviceChartData, isDarkMode, selectedMetric, processedDevices, chartsPerRow, timeRange]);

    if (processedDevices.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 'calc(100vh - 300px)',
                    textAlign: 'center',
                }}
            >
                <Paper sx={{ p: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        No charts to display
                    </Typography>
                    <Typography color="text.secondary">
                        Please use the "Selection Mode" in the Control Panel to select devices and then click "Load Charts".
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Grid container spacing={2}>
            {processedDevices.map((device) => (
                <Grid item xs={12} md={12/chartsPerRow} lg={12/chartsPerRow} key={device.box}>
                    <Paper sx={{ p: 2 }}>
                        {deviceLoadings[device.box] ? (
                            <Skeleton 
                                variant="text" 
                                width={100} 
                                sx={{ 
                                    mb: 1,
                                    bgcolor: theme => theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.05)' 
                                        : 'rgba(0, 0, 0, 0.05)' 
                                }} 
                            />
                        ) : (
                            <Typography variant="h6" gutterBottom>
                                Box {device.box}
                            </Typography>
                        )}
                        <Box sx={{ height: '200px', position: 'relative' }}>
                            {deviceLoadings[device.box] ? (
                                <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                                }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <div
                                    ref={el => chartRefs.current[device.box] = el}
                                    style={{ width: '100%', height: '100%' }}
                                />
                            )}
                        </Box>
                    </Paper>
                </Grid>
            ))}
        </Grid>
    );
};

export default DeviceCharts;